import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import useIsMobile from "../lib/useIsMobile";
import { isMobileAllowedPath, MOBILE_HOME } from "../lib/mobileRoutes";

/**
 * MobileRouteGuard — Mobile PWA Batch B3 (Mobile ARIA Phase 1 refresh)
 *
 * On smartphone viewports (≤ 767px), if the user opens a route that is not
 * in the mobile-allowed set, redirect them to Ask the Bench (mobile home)
 * with a friendly toast explaining why.
 *
 * Desktop / tablet viewports (≥ 768px) are unaffected — full app is served.
 *
 * Mobile ARIA Phase 1 refinements
 * --------------------------------
 *  • Defect A repair — Do NOT stack the "workflow available on desktop"
 *    toast on top of the ARIA login greeting. If a fresh login greeting
 *    is pending or was just delivered, we suppress the mobile-redirect
 *    toast for a short window so the clinician actually sees "Welcome
 *    back". The redirect itself still happens; only the extra toast is
 *    withheld.
 *  • Idempotent — sonner's `id: "mobile-redirect"` already dedupes, and
 *    we defensively dismiss any stale prior toast before showing a new
 *    one so a route thrash cannot stack them.
 */

// How long after login we treat the greeting as "just happened" and
// suppress the mobile-redirect toast. The greeting itself is one-shot
// per session, so this window is intentionally short.
const GREETING_QUIET_MS = 5000;

// Returns true when the ARIA login greeting has just been (or is about
// to be) delivered — used to hold back the mobile-redirect toast.
function _isGreetingActive() {
  try {
    const flag = sessionStorage.getItem("aria_greet_pending");
    if (flag === "pending") return true;
    if (flag !== "done") return false;
    const t = Number(sessionStorage.getItem("aria_greet_done_at")) || 0;
    return t > 0 && Date.now() - t < GREETING_QUIET_MS;
  } catch (_) {
    return false;
  }
}

export default function MobileRouteGuard({ children }) {
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isMobile) return;
    if (isMobileAllowedPath(location.pathname)) return;
    // Suppress the redirect toast while the login greeting is showing
    // (Defect A). The redirect still happens so the clinician lands on
    // the mobile home; only the additional toast is withheld.
    if (!_isGreetingActive()) {
      try { toast.dismiss("mobile-redirect"); } catch (_) { /* ignore */ }
      toast.info(
        "That workflow is available on desktop or tablet. On mobile you have Ask the Bench and Case Intake.",
        { id: "mobile-redirect", duration: 4500 },
      );
    }
    navigate(MOBILE_HOME, { replace: true });
  }, [isMobile, location.pathname, navigate]);

  return children;
}
